import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button } from '@upmc/ims.component-library'; 

import classnames from 'classnames';
import fileDownload from 'js-file-download';
import Card from '../../../components/UI/MDC/Card/Card';
import {
	Headline6,
	Subtitle2,
} from '../../../components/UI/MDC/Typography/Typography';
import MaterialIcon from '../../../components/UI/MDC/Icon/Icon';
import StyleAuxiliary from '../../../hoc/Auxiliary/StyleAuxiliary/StyleAuxiliary';
import WithLoadingHandler from '../../../hoc/withLoadingHandler/withLoadingHandler';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import '../../../components/UI/Input/PersonSearch/PersonSearch.scss';

import { addMessage, clearMessages } from '../../../store/ancillary/actions';
import {
	ELEMENT_NAMES,
	CSS_CLASSES,
} from '../../../components/UI/Input/PersonSearch/_constants';
import { IDENTIFIER_TYPES } from './_constants';
import { WrapImsPacket } from '../../../store/utility';

import { UserUploadPropTypes } from './_types';
import axiosInstance from '../../../services/axios/axios';
import { DataTable } from '../../../components/UI/MDC/DataTable/DataTable';
import FileUpload from '../../../components/UI/Input/FileUpload/FileUpload';

/**
 * User Upload Component:
 * Wrapper component to accept files containing expected identifiers and receive data about both invalid and valid results to be displayed.
 * @param {object} props
 */
export const UserUpload = props => {
	const dispatch = useDispatch();
	const dataTableRef = useRef(null);
	const [hasUploaded, setHasUploaded] = useState(false);
	const [identifierType, setIdentifierType] = useState(
		IDENTIFIER_TYPES.NETWORK_ID
	);
	const [isLoading, setIsLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState(null);
	const [hasScrolledToDataTable, setHasScrolledToDataTable] = useState(false);
	const VALIDATION_THRESHOLD_MIN = 3;
	const VALIDATION_THRESHOLD_MAX = 200;
	const VALIDATION_HEADER_COUNT = 1;

	/**
	 * List of headers generated by `IDENTIFIER_TYPES` to be used by dropdown and `DataTable``.
	 */
	const headers = Object.values(IDENTIFIER_TYPES).map(header => {
		return {
			Value: header,
			props: {},
		};
	});

	/**
	 * watch:  data-table rows from props
	 * if there are no rows and the upload button has been pressed, set error message and reset upload status
	 */
	useEffect(() => {
		if (props && props.dataTable && props.dataTable.rows) {
			if (props.dataTable.rows.length === 0) {
				if (hasUploaded) {
					setErrorMessage(
						'You have removed or resolved the last invalid identifier entry.  Please review your selections and proceed or upload another file.'
					);
					setHasUploaded(false);
				}
			} else {
				if (
					dataTableRef &&
					dataTableRef.current &&
					!hasScrolledToDataTable
				) {
					dataTableRef.current.scrollIntoView({
						behavior: 'smooth',
						block: 'start',
					});
					setHasScrolledToDataTable(true);
				}
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.dataTable.rows]);

	/**
	 * @function onRowClickedHandler
	 * Sends IMSPacket with the selected row data to parent `onRowClickedHandler` when row is interacted with.
	 * @param {object} row data representing the row clicked
	 */
	const onRowClickedHandler = row => {
		props.dataTable &&
			props.dataTable.onRowClickedHandler &&
			props.dataTable.onRowClickedHandler(WrapImsPacket(row));
	};

	/**
	 * @function onCellChangedHandler
	 * Wrapper for parent datatable's onTextFieldChangedHandler() invoked when invalid entry is edited.
	 * @param {ChangeEvent<HTMLInputElement>} e event created by the change
	 * @param {object} row row object
	 * @param {string} key key of the row object
	 */
	const onCellChangedHandler = (e, row, key) => {
		props.dataTable.onTextFieldChangedHandler(e, row, key);
	};

	/**
	 * @function onFileUploadHandler
	 * Handler for when file is uploaded.
	 * Checks validation of file and, if valid, sends request to parent file upload handler.
	 * @param {object} payload the file contents itself
	 */
	const onFileUploadHandler = async payload => {
		//validate content requirements
		const wasThresholdMet =
			payload.fileContent.length >= VALIDATION_THRESHOLD_MIN;
		if (!wasThresholdMet) {
			setErrorMessage(
				`Insufficient row count: A minimum of ${VALIDATION_THRESHOLD_MIN} identifiers is supported. Please add users.`
			);
		}

		const wasThresholdExceeded =
			payload.fileContent.length > VALIDATION_THRESHOLD_MAX;
		if (wasThresholdExceeded) {
			setErrorMessage(
				`Excessive row count:  A maximum of ${VALIDATION_THRESHOLD_MAX} users is supported. Please remove users.`
			);
		}

		//validate data
		let isExpectedData = true;
		if (wasThresholdMet && !wasThresholdExceeded) {
			//validate headers
			const fileHeaders = payload.fileHeaders.map(fileHeader =>
				fileHeader.replace(/_/g, ' ').trim()
			);

			const isExpectedColumnCount =
				VALIDATION_HEADER_COUNT === fileHeaders.length;
			if (isExpectedColumnCount) {
				const isExpectedHeader = fileHeaders.every(
					fileHeader =>
						fileHeader &&
						Object.values(IDENTIFIER_TYPES).includes(fileHeader)
				);
				if (!isExpectedHeader) {
					setErrorMessage(
						`Unexpected header row detected.  Please download and use the template.`
					);
					isExpectedData = false;
				}
			} else {
				setErrorMessage(
					`Invalid data detected.  This could be caused by having too many columns.`
				);
				isExpectedData = false;
			}

			//validate data body
			if (isExpectedData) {
				let ids = payload.fileContent.map((row, index) => {
					const arrRow = row.split(',');
					if (arrRow.length !== 1) {
						isExpectedData = false;
						setErrorMessage(
							`Invalid Data:  A row (row ${
								index + 1
							}) in your file has more data than expected.`
						);
					}
					return arrRow[0].trim();
				});

				//Check for duplicated identifiers.  If detected, remove duplicates and notify user.
				const duplicatedIds = ids.reduce(
					(acc, currentValue, index, array) => {
						if (
							array.indexOf(currentValue) !== index &&
							!acc.includes(currentValue)
						) {
							acc.push(currentValue);
						}
						return acc;
					},
					[]
				);
				if (duplicatedIds.length > 0) {
					setErrorMessage(
						`The following identifiers have been removed because they were listed more than once in the file:  ${duplicatedIds.join(
							', '
						)}`
					);
					ids = ids.reduce((acc, currentValue) => {
						if (!acc.includes(currentValue)) {
							acc.push(currentValue);
						}
						return acc;
					}, []);
				}

				if (
					wasThresholdMet &&
					!wasThresholdExceeded &&
					isExpectedData
				) {
					try {
						setIsLoading(true);
						// duplicate identifier(s) the only error permitting an upload, all other errors to be reset
						if (!duplicatedIds.length > 0) {
							setErrorMessage(null);
						}
						await props.onValidateHandler(identifierType, ids);
						setHasUploaded(true);
						setHasScrolledToDataTable(false);
					} catch (error) {
						raiseErrorBanner(error);
					} finally {
						setIsLoading(false);
					}
				}
			}
		}
	};

	/**
	 * Method for dispatching action to raise banner with error message.
	 * @param {Object} error error raised.
	 * @returns void.
	 */
	const raiseErrorBanner = error => {
		let message =
			error && error.response && error.response.data
				? error.response.data.payload
				: error.message;
		dispatch(clearMessages());
		dispatch(
			addMessage({
				Key: 3,
				Value: message,
			})
		);
	};

	/**
	 * DataTable output of invalid results from file upload.
	 * Shown only if there are rows to display and no errors have been detected.
	 */
	const dataTable = (
		<div ref={dataTableRef}>
			<WithLoadingHandler
				isLoading={isLoading}
				showChildren={hasUploaded && props.dataTable.rows.length > 0}
			>
				<DataTable
					{...props.dataTable}
					headers={headers}
					onRowClickedHandler={
						props.multiPerson
							? null
							: row => onRowClickedHandler(row)
					}
					stickyRows={1}
					validation={{
						maximumThreshold: 200,
						onCellChangedHandler: (e, row, key) =>
							onCellChangedHandler(e, row, key),
					}}
					data-test={ELEMENT_NAMES.DATA_TABLE}
				/>
			</WithLoadingHandler>
		</div>
	);

	return (
		<Card
			className={classnames(props.className, CSS_CLASSES.CONTAINER, {
				[`${CSS_CLASSES.FIT_CONTAINER}`]: !dataTable,
			})}
			xlarge
		>
			<StyleAuxiliary classes={CSS_CLASSES.INPUT_CONTAINER}>
				<Headline6 data-test={ELEMENT_NAMES.LABEL}>
					{props.label}
				</Headline6>
				<FormControl>
					<InputLabel id='user-upload__select--identifier-type__label'>
						Identifier
					</InputLabel>
					<Select
						id='user-upload__select--identifier-type'
						label='Identifier'
						labelId='user-upload__select--identifier-type__label'
						onChange={e => {
							setIdentifierType(e.target.value);
						}}
						value={identifierType}
					>
						{Object.values(IDENTIFIER_TYPES).map(type => (
							<MenuItem value={type}>{type}</MenuItem>
						))}
					</Select>
				</FormControl>
				<Button
					sx={{ width: '16rem' }}
					disabled={props.disabled}
					id='user-upload__button--download'
					onClick={() => {
						axiosInstance
							.get(
								'api/Components/UserUpload/Template/' +
									identifierType
							)
							.then(response => {
								let filenameModifier = '';
								switch (identifierType) {
									case IDENTIFIER_TYPES.EMAIL:
										filenameModifier = 'Email';
										break;
									case IDENTIFIER_TYPES.EMPLOYEE_ID:
										filenameModifier = 'Employee_ID';
										break;
									case IDENTIFIER_TYPES.ESO_ID:
										filenameModifier = 'UPMC_ESignOn_ID';
										break;
									case IDENTIFIER_TYPES.NETWORK_ID:
										filenameModifier = 'NTID';
										break;
									default:
										filenameModifier = 'Default';
										break;
								}
								fileDownload(
									response.data,
									`IMS_User_Upload_${filenameModifier}.csv`
								);
							})
							.catch(error => {
								raiseErrorBanner(error);
							});
					}}
					startIcon={<MaterialIcon icon='file_download' />}
					variant='text'
				>
					Download Template
				</Button>
				<FileUpload
					disabled={false}
					fileTypes={'.xls,.txt,.csv'}
					fileMIMETypes={
						'application/vnd.ms-excel,text/csv,text/plain'
					}
					onClickHandler={payload => onFileUploadHandler(payload)}
					parseRegex={new RegExp(/(\r?\n)/)}
					resetSelection={!hasUploaded}
				/>
				{props.errorMessage || errorMessage ? (
					<Subtitle2
						className={CSS_CLASSES.INVALID}
						data-test={ELEMENT_NAMES.MESSAGE_ERROR}
					>
						{props.errorMessage
							? props.errorMessage
							: errorMessage
							? errorMessage
							: null}
					</Subtitle2>
				) : null}
			</StyleAuxiliary>
			<WithLoadingHandler isLoading={props.isLoading}>
				{dataTable}
			</WithLoadingHandler>
		</Card>
	);
};

UserUpload.propTypes = UserUploadPropTypes;
